"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { io, type Socket } from "socket.io-client";
import { useTranslation } from "react-i18next";
import {
  api,
  type PriceHistoryInterval,
  type PriceHistoryPoint,
} from "@/lib/api";
import { Skeleton } from "@/components/Skeleton";

interface IncomingTradeEvent {
  marketSlug: string;
}

interface LivePriceChartProps {
  slug: string;
  // Used as the "now" point if the API returns zero buckets (covers
  // the brief gap between a market's first trade landing and the
  // bucket-aggregated row appearing at the next minute boundary).
  fallbackPriceCents: number;
}

const INTERVALS: { id: PriceHistoryInterval; label: string }[] = [
  { id: "1h", label: "1H" },
  { id: "6h", label: "6H" },
  { id: "1d", label: "1D" },
  { id: "1w", label: "1W" },
  { id: "1m", label: "1M" },
  { id: "all", label: "ALL" },
];

// 2026-05-09 — polling cadence per interval. WS is broken on staging
// (cloudflared tunnel issue #1607); polling keeps the chart live
// regardless of WS state. Faster cadences for tighter time-frames so
// the 1H view actually feels alive.
const POLL_INTERVAL_MS: Record<PriceHistoryInterval, number> = {
  "1h": 5_000,
  "6h": 10_000,
  "1d": 15_000,
  "1w": 30_000,
  "1m": 60_000,
  all: 60_000,
};

/**
 * Time-series chart backed by GET /v1/markets/:slug/history.
 *
 * 2026-05-09 redesign per founder direction ("looks dead, want
 * Polymarket / Kalshi feel"):
 * - Polling fallback alongside the WS subscriber so the chart updates
 *   as new trades land regardless of WS health.
 * - Y-axis padded more aggressively (15¢ instead of 5¢) so the line
 *   doesn't look frozen on a sparse-data market.
 * - Price-axis labels (min / max / last) on the right side so the
 *   range is visible.
 * - Hover tooltip showing time + price at cursor.
 * - Trade-point markers — small dots at each data point.
 * - Volume bars rendered under the line for activity context.
 * - "Last updated" caption + a flash highlight when a new point lands.
 *
 * Owns its own interval-tab state. Re-fetches on interval change +
 * on the polling cadence + on every market:trade WS event.
 */
export function LivePriceChart({
  slug,
  fallbackPriceCents,
}: LivePriceChartProps) {
  const { t } = useTranslation();
  const [interval, setInterval] = useState<PriceHistoryInterval>("1d");
  // Actual interval the rendered chart is sourced from. May be wider
  // than the user's selection when the smart-fallback walks up the
  // INTERVAL_ORDER looking for data.
  const [effectiveInterval, setEffectiveInterval] =
    useState<PriceHistoryInterval>("1d");
  const [points, setPoints] = useState<PriceHistoryPoint[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [refetchToken, setRefetchToken] = useState(0);

  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    let cancelled = false;
    setError(null);

    // Smart fallback: try the user's interval first; if < 2 points
    // come back, walk one step wider until we find data or run out.
    // Keeps low-liquidity markets / fresh markets from rendering as
    // a blank canvas.
    const order: PriceHistoryInterval[] = [
      "1h",
      "6h",
      "1d",
      "1w",
      "1m",
      "all",
    ];
    const widerInterval = (
      current: PriceHistoryInterval,
    ): PriceHistoryInterval | null => {
      const idx = order.indexOf(current);
      if (idx < 0 || idx === order.length - 1) return null;
      return order[idx + 1];
    };

    const tryFetch = async (range: PriceHistoryInterval): Promise<void> => {
      try {
        const res = await api.getMarketHistory(slug, { interval: range });
        if (cancelled) return;
        const pts = Array.isArray(res?.points) ? res.points : [];
        const wider = widerInterval(range);
        if (pts.length < 2 && wider) {
          return tryFetch(wider);
        }
        setEffectiveInterval(range);
        setPoints(pts);
      } catch (err) {
        if (cancelled) return;
        setError(
          err instanceof Error ? err.message : t("livePriceChart.loadFailed"),
        );
      }
    };
    void tryFetch(interval);
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, interval, refetchToken]);
  /* eslint-enable react-hooks/set-state-in-effect */

  // 2026-05-09 polling fallback. Cadence varies per interval. Skips
  // when the tab is hidden (don't burn the user's data plan + API
  // budget on backgrounded tabs).
  useEffect(() => {
    const cadence = POLL_INTERVAL_MS[interval];
    const handle = window.setInterval(() => {
      if (typeof document !== "undefined" && document.hidden) return;
      setRefetchToken((n) => n + 1);
    }, cadence);
    return () => window.clearInterval(handle);
  }, [interval]);

  // WS-driven live updates — sub-second when the pipe is healthy.
  // Polling fallback above keeps the chart alive when WS is down
  // (cloudflared tunnel staging issue, #1607).
  const refetchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    const apiBase = process.env.NEXT_PUBLIC_API_URL;
    if (!apiBase) return;
    const url = apiBase.replace(/\/$/, "");
    let socket: Socket | null = null;
    try {
      socket = io(url, {
        transports: ["websocket", "polling"],
        reconnection: true,
        reconnectionAttempts: 3,
        timeout: 8000,
      });
      socket.on("connect", () => {
        socket?.emit("subscribe", { slug });
      });
      socket.on("market:trade", (event: IncomingTradeEvent) => {
        if (event.marketSlug !== slug) return;
        if (refetchTimerRef.current) clearTimeout(refetchTimerRef.current);
        refetchTimerRef.current = setTimeout(() => {
          setRefetchToken((n) => n + 1);
        }, 600);
      });
      socket.on("connect_error", () => {
        // swallow — polling fallback handles updates regardless.
      });
    } catch {
      // socket-io constructor doesn't throw, but be defensive.
    }

    return () => {
      if (refetchTimerRef.current) clearTimeout(refetchTimerRef.current);
      if (socket) {
        try {
          socket.emit("unsubscribe", { slug });
          socket.disconnect();
        } catch {
          // ignore — cleanup-time errors are harmless.
        }
      }
    };
  }, [slug]);

  return (
    <div>
      <div aria-live="polite">
        {error ? (
          <ChartError message={error} />
        ) : points === null ? (
          <ChartSkeleton />
        ) : (
          <ChartCanvas points={points} fallbackPriceCents={fallbackPriceCents} />
        )}
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-2">
        <div
          className="flex gap-0.5"
          role="tablist"
          aria-label={t("livePriceChart.rangeAriaLabel")}
        >
          {INTERVALS.map(({ id, label }) => {
            const active = id === interval;
            return (
              <button
                key={id}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setInterval(id)}
                className={`rounded-[4px] px-2 py-[3px] text-[10px] font-semibold transition-colors ${
                  active
                    ? "border border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-text)]"
                    : "text-[var(--color-text3)] hover:text-[var(--color-text)]"
                }`}
              >
                {label}
              </button>
            );
          })}
        </div>
        {effectiveInterval !== interval && (
          <span
            className="text-[10px] text-[var(--color-text3)]"
            title="Selected window had limited trade activity; showing a wider window instead."
          >
            Low data · showing{" "}
            <span className="font-semibold text-[var(--color-text2)]">
              {INTERVALS.find((i) => i.id === effectiveInterval)?.label ??
                effectiveInterval.toUpperCase()}
            </span>
          </span>
        )}
      </div>
    </div>
  );
}

function ChartSkeleton() {
  const { t } = useTranslation();
  return (
    <div role="status" aria-label={t("livePriceChart.loadingAria")}>
      <Skeleton className="h-[220px] w-full rounded-md" />
    </div>
  );
}

function ChartError({ message }: { message: string }) {
  return (
    <div
      role="alert"
      className="flex h-[220px] w-full items-center justify-center rounded-md border border-dashed border-[var(--color-no)] bg-[var(--color-bg)] px-3 text-center text-[12px] text-[var(--color-no)]"
    >
      {message}
    </div>
  );
}

interface HoverState {
  index: number;
  x: number;
  y: number;
}

function ChartCanvas({
  points,
  fallbackPriceCents,
}: {
  points: PriceHistoryPoint[];
  fallbackPriceCents: number;
}) {
  const { t } = useTranslation();
  const [hover, setHover] = useState<HoverState | null>(null);
  const svgRef = useRef<SVGSVGElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // 2026-05-09 — measured-width sizing replaces the previous fixed-
  // viewBox + `preserveAspectRatio="none"` which produced the
  // "stretchy" look the founder flagged. We now render the SVG at
  // its actual rendered pixel dimensions so 1:1 stroke widths +
  // text sizes stay crisp and the chart no longer skews horizontally.
  const [width, setWidth] = useState(720);
  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const w = Math.round(entry.contentRect.width);
        if (w > 0) setWidth(w);
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Empty result => render a flat line at fallbackPriceCents so the
  // chart area doesn't collapse to height 0 mid-render. Real data
  // lands on the next interval-change or page revisit.
  const chartData = useMemo(() => {
    const series =
      points.length === 0
        ? [
            { ts: new Date().toISOString(), priceCents: fallbackPriceCents, volumeCents: "0" },
            { ts: new Date().toISOString(), priceCents: fallbackPriceCents, volumeCents: "0" },
          ]
        : points.length === 1
          ? [points[0], points[0]]
          : points;

    const prices = series.map((p) => p.priceCents);
    const volumes = series.map((p) => Number(p.volumeCents) || 0);
    return { series, prices, volumes };
  }, [points, fallbackPriceCents]);

  const { series, prices, volumes } = chartData;

  // Native pixel dimensions — no aspect distortion. Polymarket /
  // Kalshi feel: ~220px tall chart, full container width, type +
  // strokes rendered at their actual designed sizes.
  const w = width;
  const h = 220;
  const lineH = 168; // line-chart band
  const volH = 28; // volume-bar band (slightly tighter)
  const padX = 12;
  const padY = 14;
  const labelPad = 44; // right-side label gutter for price scale

  // 2026-05-09 — wider y-axis padding (15¢) so a tight cluster of
  // similar prices renders with visible curvature. Was 5¢, founder
  // reported "looks dead." Polymarket pads ~10-20% of range or ~10¢,
  // whichever is greater, so 15¢ matches their feel.
  const minPrice = Math.min(...prices);
  const maxPrice = Math.max(...prices);
  const yMin = Math.max(0, Math.floor(minPrice - 15));
  const yMax = Math.min(100, Math.ceil(maxPrice + 15));
  const yRange = yMax - yMin || 1;

  const maxVol = Math.max(...volumes, 1);

  const innerW = Math.max(1, w - padX * 2 - labelPad);
  const toX = (i: number) =>
    padX + (i * innerW) / Math.max(1, series.length - 1);
  const toLineY = (v: number) =>
    padY + lineH - ((v - yMin) / yRange) * (lineH - padY);
  const toVolY = (v: number) =>
    padY + lineH + 4 + (volH - 4) * (1 - v / maxVol);

  const startV = prices[0];
  const endV = prices[prices.length - 1];
  // Match the platform's yes/no token colors for outcome cues, so
  // the chart reads from the same palette as the trade panel.
  const color = endV >= startV ? "var(--color-yes)" : "var(--color-no)";
  const colorRaw = endV >= startV ? "#2d8a5d" : "#b34a4a";

  const pathD = prices
    .map(
      (p, i) => `${i === 0 ? "M" : "L"}${toX(i).toFixed(1)},${toLineY(p).toFixed(1)}`,
    )
    .join(" ");
  const areaD = `${pathD} L${toX(prices.length - 1).toFixed(1)},${padY + lineH} L${toX(0).toFixed(1)},${padY + lineH} Z`;

  const gradientId = `live-chart-gradient-${endV}-${startV}`;

  function handleMove(e: React.MouseEvent<SVGSVGElement>) {
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const xRatio = (e.clientX - rect.left) / rect.width;
    const xViewport = xRatio * w;
    const indexF = Math.max(
      0,
      Math.min(
        series.length - 1,
        ((xViewport - padX) / innerW) * (series.length - 1),
      ),
    );
    const i = Math.round(indexF);
    setHover({ index: i, x: toX(i), y: toLineY(prices[i]) });
  }
  function handleLeave() {
    setHover(null);
  }

  // Y-axis ticks — 3 reference lines: yMax, mid, yMin.
  const yTicks = [
    yMax,
    Math.round((yMax + yMin) / 2),
    yMin,
  ].filter((v, i, arr) => arr.indexOf(v) === i);

  // Geist Mono via the same CSS variable the rest of the app uses.
  // Direct `monospace` falls back to whatever the OS picks (Menlo
  // on macOS, Consolas on Windows) — the chart looked off-brand.
  const monoStack = "var(--font-mono), ui-monospace, monospace";

  return (
    <div ref={containerRef} className="relative">
      <svg
        ref={svgRef}
        width={w}
        height={h}
        viewBox={`0 0 ${w} ${h}`}
        role="img"
        aria-label={t("livePriceChart.chartAria")}
        onMouseMove={handleMove}
        onMouseLeave={handleLeave}
      >
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={colorRaw} stopOpacity="0.22" />
            <stop offset="100%" stopColor={colorRaw} stopOpacity="0" />
          </linearGradient>
        </defs>

        {/* Y-axis grid lines + labels (right-side gutter) */}
        {yTicks.map((tick) => {
          const y = toLineY(tick);
          return (
            <g key={tick}>
              <line
                x1={padX}
                x2={w - labelPad}
                y1={y}
                y2={y}
                stroke="var(--color-border)"
                strokeOpacity="0.55"
                strokeDasharray="1 4"
              />
              <text
                x={w - labelPad + 6}
                y={y + 3}
                fontSize="10"
                fontWeight="500"
                fill="var(--color-text3)"
                fontFamily={monoStack}
                style={{ fontVariantNumeric: "tabular-nums" }}
              >
                {tick}¢
              </text>
            </g>
          );
        })}

        {/* Volume bars under the line */}
        {volumes.map((v, i) => {
          if (v === 0) return null;
          const x = toX(i);
          const yTop = toVolY(v);
          const yBottom = padY + lineH + volH;
          const barW = Math.max(1.5, innerW / Math.max(1, series.length) - 2);
          return (
            <rect
              key={`vol-${i}`}
              x={x - barW / 2}
              y={yTop}
              width={barW}
              height={Math.max(0, yBottom - yTop)}
              fill={color}
              fillOpacity="0.28"
              rx="0.5"
            />
          );
        })}

        {/* Price area + line */}
        <path d={areaD} fill={`url(#${gradientId})`} />
        <path
          d={pathD}
          fill="none"
          stroke={color}
          strokeWidth="1.75"
          strokeLinecap="round"
          strokeLinejoin="round"
        />

        {/* Trade-point markers (skip dense series for legibility) */}
        {prices.length <= 80 &&
          prices.map((p, i) => (
            <circle
              key={`pt-${i}`}
              cx={toX(i)}
              cy={toLineY(p)}
              r="1.75"
              fill={color}
              fillOpacity={0.55}
            />
          ))}

        {/* Latest-point emphasis: halo + filled dot + ring for the
            "live" pulse look used on Polymarket / Kalshi cards. */}
        <circle
          cx={toX(prices.length - 1)}
          cy={toLineY(prices[prices.length - 1])}
          r="7"
          fill={color}
          fillOpacity="0.18"
        />
        <circle
          cx={toX(prices.length - 1)}
          cy={toLineY(prices[prices.length - 1])}
          r="3.5"
          fill={color}
        />
        <circle
          cx={toX(prices.length - 1)}
          cy={toLineY(prices[prices.length - 1])}
          r="3.5"
          fill="none"
          stroke="var(--color-bg)"
          strokeWidth="1.25"
        />

        {/* Hover crosshair + marker */}
        {hover && (
          <g>
            <line
              x1={hover.x}
              x2={hover.x}
              y1={padY}
              y2={padY + lineH + volH}
              stroke="var(--color-text3)"
              strokeOpacity="0.55"
              strokeWidth="1"
              strokeDasharray="2 3"
            />
            <circle
              cx={hover.x}
              cy={hover.y}
              r="4"
              fill="var(--color-bg)"
              stroke={color}
              strokeWidth="1.75"
            />
          </g>
        )}
      </svg>

      {/* Hover tooltip — positioned in pixel space (matches the SVG's
          rendered coordinates 1:1 since we no longer stretch). */}
      {hover && series[hover.index] && (
        <div
          className="pointer-events-none absolute rounded-md border border-[var(--color-border)] bg-[var(--color-bg)]/95 px-2.5 py-1.5 text-[11px] shadow-lg backdrop-blur-sm"
          style={{
            left: hover.x,
            top: 4,
            transform: "translateX(-50%)",
            color: "var(--color-text)",
            whiteSpace: "nowrap",
            fontFamily: "var(--font-sans), system-ui, sans-serif",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          <span className="font-semibold tracking-tight" style={{ fontFamily: monoStack }}>
            {series[hover.index].priceCents}¢
          </span>
          <span className="ml-2 text-[var(--color-text3)]">
            {new Date(series[hover.index].ts).toLocaleString(undefined, {
              hour: "2-digit",
              minute: "2-digit",
              month: "short",
              day: "numeric",
            })}
          </span>
        </div>
      )}
    </div>
  );
}
